import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormSelect } from 'components/common';

const fmtV = (v) => (v || '').replace(/^v/i, '');
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { FiPlus, FiEdit2, FiTrash2, FiEyeOff, FiUploadCloud, FiAlertCircle, FiCheck } from 'react-icons/fi';
import Drawer     from 'components/common/Drawer';
import FormInput  from 'components/common/FormInput';
import Button     from 'components/common/Button';
import TinymceEditor from 'components/common/TinymceEditor';
import confirmAlert  from 'components/common/confirmAlert';
import {
  fetchAllChangelogEntries,
  createChangelogEntry,
  updateChangelogEntry,
  publishChangelogEntry,
  unpublishChangelogEntry,
  deleteChangelogEntry,
} from 'redux/documentation/changelogEntrySlice';

// ── Array ↔ HTML helpers ──────────────────────────────────────────────────────
// Converts string[] → <ul><li>…</li></ul> for TinyMCE, and back on save.

const arrayToHtml = (arr = []) =>
  arr.length ? `<ul>${arr.map(i => `<li>${i}</li>`).join('')}</ul>` : '';

const htmlToArray = (html = '') => {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Collect ALL block content — both <li> and <p> so mixed typing is never dropped
  const items = [];
  doc.querySelectorAll('li, p').forEach(el => {
    const text = el.textContent.trim();
    if (text) items.push(text);
  });
  if (items.length > 0) return items;
  const bodyText = doc.body.textContent.trim();
  return bodyText ? [bodyText] : [];
};

// ── TinyMCE config presets ────────────────────────────────────────────────────

const LIST_INIT = {
  menubar:    false,
  statusbar:  false,
  plugins:    'lists',
  toolbar:    'bullist numlist | bold italic | removeformat',
  content_style: 'body { font-family: Inter, sans-serif; font-size: 13px; margin: 8px; } ul { padding-left: 20px; margin: 0; } li { margin-bottom: 4px; }',
  setup: (editor) => {
    editor.on('init', () => {
      if (!editor.getContent({ format: 'text' }).trim()) {
        editor.execCommand('InsertUnorderedList');
      }
    });
  },
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    published: 'tw-bg-emerald-100 tw-text-emerald-700 tw-border tw-border-emerald-200',
    draft:     'tw-bg-slate-100 tw-text-slate-600 tw-border tw-border-slate-200',
    scheduled: 'tw-bg-amber-100 tw-text-amber-700 tw-border tw-border-amber-200',
  };
  const icons = {
    published: <FiCheck size={10} className="tw-inline tw-mr-1" />,
    draft:     null,
    scheduled: null,
  };
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-bold tw-rounded-xl tw-uppercase tw-tracking-wide tw-transition ${map[status] || map.draft}`}>
      {icons[status]}{status}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string,
};

// ── List section editor (array ↔ TinyMCE) ────────────────────────────────────
// Uncontrolled: uses initialValue so TinyMCE owns its state — no cursor jumps.
// The parent ChangelogForm key ensures remount with fresh data on each open.
const ListEditor = ({ label, value = [], onChange }) => (
  <div>
    <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">{label}</label>
    <div className="tw-rounded-md tw-overflow-hidden tw-border tw-border-slate-300">
      <TinymceEditor
        initialValue={arrayToHtml(value)}
        handleChange={html => onChange(htmlToArray(html))}
        height="130px"
        initOverride={LIST_INIT}
      />
    </div>
  </div>
);

ListEditor.propTypes = {
  label: PropTypes.node,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

// ── Status options ────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft'     },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
];

// ── Empty form ────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  version: '', title: '', description: '', released_at: '', status: 'draft',
  highlights: [], added: [], modifications: [], fixed: [], deprecated: [], security: [],
  upgrade_guide_url: '',
};

// ── Changelog form inside Drawer ──────────────────────────────────────────────
const ChangelogForm = ({ entry, show, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const saving   = useSelector(s => s.changelog_entry.saving);
  const isEdit   = !!entry;

  const [form, setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!show) return;
    if (entry) {
      const a = entry.attributes;
      setForm({
        version:           a.version,
        title:             a.title || '',
        description:       a.description || '',
        released_at:       a.released_at ? a.released_at.substring(0, 10) : '',
        status:            a.status || 'draft',
        highlights:        a.highlights || [],
        added:             a.added || [],
        modifications:     a.modifications || [],
        fixed:             a.fixed || [],
        deprecated:        a.deprecated || [],
        security:          a.security || [],
        upgrade_guide_url: a.upgrade_guide_url || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors([]);
  }, [show, entry]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setErrors([]);
    const result = await dispatch(
      isEdit
        ? updateChangelogEntry({ version: entry.attributes.version, payload: form })
        : createChangelogEntry(form)
    );
    if (result.meta?.requestStatus === 'rejected') {
      const errs = result.payload?.errors;
      setErrors(errs?.length ? errs : ['An error occurred. Please try again.']);
    } else {
      onSaved(result.payload, isEdit);
    }
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={handleSubmit} loading={saving}>
        {isEdit ? 'Save Changes' : 'Create Entry'}
      </Button>
    </>
  );

  return (
    <Drawer
      show={show}
      onHide={onClose}
      title={isEdit ? `Edit — v${fmtV(entry?.attributes?.version)}` : 'New Changelog Entry'}
      footer={footer}
      width="560px"
      minWidth="360px"
    >
      <div className="tw-space-y-5">

        {/* Errors */}
        {errors.length > 0 && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-text-sm tw-text-red-700">
            <FiAlertCircle className="tw-shrink-0 tw-mt-0.5" size={15} />
            <ul className="tw-list-disc tw-pl-3 tw-space-y-0.5">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {/* Version + Date */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <FormInput
            label="Version" required
            value={form.version}
            onChange={e => set('version', e.target.value)}
            placeholder="4.3.0"
            disabled={isEdit}
          />
          <FormInput
            label="Release Date" type="date" required
            value={form.released_at}
            onChange={e => set('released_at', e.target.value)}
          />
        </div>

        {/* Title */}
        <FormInput
          label="Title" required
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="Performance & Security Improvements"
        />

        {/* Status + Upgrade Guide */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <FormSelect
            label="Status"
            value={form.status}
            onChange={e => set('status', e.target.value)}
            options={STATUS_OPTIONS}
          />
          <div>
            <FormInput
              label="User guide URL (optional)" type="url"
              value={form.upgrade_guide_url}
              onChange={e => set('upgrade_guide_url', e.target.value)}
              placeholder="https://portal.pebsteel.com/documentation/user-guide/…"
            />
            {/* Người viết changelog cần biết ô này đi ra ngoài chứ không chỉ nằm
                trong trang release notes. */}
            <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-slate-400">
              Adds a “Read the user guide” button to the release email, next to the release notes link.
              Leave empty if this release has no guide.
            </p>
          </div>
        </div>

        {/* Description — TinyMCE rich text */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">
            Description
          </label>
          <div className="tw-rounded-md tw-overflow-hidden tw-border tw-border-slate-300">
            <TinymceEditor
              value={form.description}
              handleChange={v => set('description', v)}
              height="120px"
            />
          </div>
        </div>

        {/* Change sections — TinyMCE list editors */}
        <div className="tw-border-t tw-border-slate-100 tw-pt-5 tw-space-y-4">
          <p className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider">
            Change Sections
          </p>
          <ListEditor key={`${form.version}-highlights`}    label="✨ Highlights"  value={form.highlights}    onChange={v => set('highlights', v)} />
          <ListEditor key={`${form.version}-added`}        label="➕ Added"       value={form.added}         onChange={v => set('added', v)} />
          <ListEditor key={`${form.version}-modifications`} label="✏️ Changed"    value={form.modifications} onChange={v => set('modifications', v)} />
          <ListEditor key={`${form.version}-fixed`}        label="🔧 Fixed"       value={form.fixed}         onChange={v => set('fixed', v)} />
          <ListEditor key={`${form.version}-security`}     label="🔒 Security"    value={form.security}      onChange={v => set('security', v)} />
          <ListEditor key={`${form.version}-deprecated`}   label="⚠️ Deprecated"  value={form.deprecated}    onChange={v => set('deprecated', v)} />
        </div>
      </div>
    </Drawer>
  );
};

ChangelogForm.propTypes = {
  entry: PropTypes.shape({
    attributes: PropTypes.shape({
      version: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      released_at: PropTypes.string,
      status: PropTypes.string,
      highlights: PropTypes.arrayOf(PropTypes.string),
      added: PropTypes.arrayOf(PropTypes.string),
      modifications: PropTypes.arrayOf(PropTypes.string),
      fixed: PropTypes.arrayOf(PropTypes.string),
      deprecated: PropTypes.arrayOf(PropTypes.string),
      security: PropTypes.arrayOf(PropTypes.string),
      upgrade_guide_url: PropTypes.string,
    }).isRequired,
  }),
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ── Admin panel ───────────────────────────────────────────────────────────────
const ChangelogAdminPanel = ({ onEntrySelect, onEntryUpdated }) => {
  const dispatch = useDispatch();
  const { data: entries, loading } = useSelector(s => s.changelog_entry);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  useEffect(() => { dispatch(fetchAllChangelogEntries()); }, [dispatch]);

  const openCreate = () => { setEditEntry(null); setShowForm(true); };
  const openEdit   = (entry) => { setEditEntry(entry); setShowForm(true); };
  const closeForm  = () => { setShowForm(false); setEditEntry(null); };

  const handlePublishToggle = (entry) => {
    const isPublished = entry.attributes.status === 'published';
    const version = entry.attributes.version;   // raw — used for the API lookup
    const vLabel  = `v${fmtV(version)}`;         // display — never double-prefixed
    confirmAlert({
      title:   `${isPublished ? 'Unpublish' : 'Publish'} ${vLabel}`,
      message: isPublished
        ? `Are you sure you want to unpublish ${vLabel}? It will no longer be visible to users.`
        // Publish gửi cả mail chứ không chỉ chuông trong app — nói đúng để người
        // publish biết mình đang gửi ra ngoài, và nhắc kiểm tra link guide trước.
        : `Are you sure you want to publish ${vLabel}? Active users will receive an in-app notification and the release email${
            entry.attributes.upgrade_guide_url ? ', including the user guide link' : ' (no user guide link is set)'
          }.`,
      type:    isPublished ? 'warning' : 'info',
      onConfirm: async () => {
        const action = isPublished ? unpublishChangelogEntry : publishChangelogEntry;
        const result = await dispatch(action(version));
        if (result.meta?.requestStatus === 'rejected') {
          toast.error(`Failed to ${isPublished ? 'unpublish' : 'publish'} ${vLabel}`);
        } else {
          dispatch(fetchAllChangelogEntries());
          if (result.payload) onEntryUpdated?.(result.payload);
          toast.success(
            isPublished
              ? `${vLabel} unpublished successfully`
              : `${vLabel} published — active users will be notified`
          );
        }
      },
    });
  };

  const handleDelete = (entry) => {
    const version = entry.attributes.version;   // raw — used for the API lookup
    const vLabel  = `v${fmtV(version)}`;
    confirmAlert({
      title:   `Delete ${vLabel}`,
      message: 'This action cannot be undone.',
      type:    'danger',
      onConfirm: () => {
        dispatch(deleteChangelogEntry(version))
          .then(result => {
            if (result.meta?.requestStatus === 'rejected') {
              toast.error(`Failed to delete ${vLabel}`);
            } else {
              toast.success(`${vLabel} deleted`);
            }
          });
      },
    });
  };

  const handleSaved = (saved, isEdit) => {
    closeForm();
    dispatch(fetchAllChangelogEntries());
    const v = `v${fmtV(saved?.attributes?.version)}`;
    toast.success(isEdit ? `${v} updated successfully` : `${v} created successfully`);
    if (saved) onEntryUpdated?.(saved);
  };

  return (
    <>
      {/* Full height + sticky: danh sách chạy hết màn hình thay vì một hộp cao
          70vh nằm lửng, và ở lại khi cuộn phần chi tiết bên phải. */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden lg:tw-sticky lg:tw-top-4 lg:tw-flex lg:tw-flex-col lg:tw-h-[calc(100dvh-2rem)]">
        {/* Header */}
        <div className="tw-shrink-0 tw-px-5 tw-py-4 tw-border-b tw-border-slate-100 tw-bg-gradient-to-r tw-from-slate-50 tw-via-white tw-to-transparent tw-flex tw-items-center tw-justify-between tw-gap-3">
          <p className="tw-text-xs tw-font-bold tw-text-slate-600 tw-uppercase tw-tracking-widest tw-flex tw-items-center tw-gap-2.5">
            <span className="tw-w-2 tw-h-2 tw-bg-blue-500 tw-rounded-xl tw-shadow-lg tw-shadow-blue-500/50"></span>
            Admin — All Entries
          </p>
          <Button size="sm" variant="primary" onClick={openCreate} className="tw-gap-2 tw-flex tw-items-center tw-font-semibold tw-rounded tw-shadow-sm hover:tw-shadow-md tw-transition-all">
            <FiPlus size={15} /> New Entry
          </Button>
        </div>

        {/* List */}
        <div className="changelog-scroll tw-max-h-[70vh] tw-overflow-y-auto tw-overflow-x-hidden lg:tw-flex-1 lg:tw-min-h-0 lg:tw-max-h-none">
          {loading ? (
            <p className="tw-text-sm tw-text-slate-400 tw-text-center tw-py-12">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="tw-text-sm tw-text-slate-400 tw-text-center tw-py-12">No entries yet</p>
          ) : (
              <div className="tw-divide-y tw-divide-slate-100/60">
                {entries.map(entry => {
                  const { version, title, release_date_formatted, status } = entry.attributes;
                  const isPublished = status === 'published';
                  return (
                    <div
                      key={entry.id}
                      className="tw-px-4 tw-py-3.5 hover:tw-bg-blue-50/40 tw-transition-colors tw-duration-150"
                    >
                      {/* Clickable summary — selects the entry for preview */}
                      <div className="tw-cursor-pointer" onClick={() => onEntrySelect?.(entry)}>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5">
                          <span className="tw-font-mono tw-text-sm tw-font-bold tw-text-slate-800 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-sm tw-border tw-border-slate-200/60">
                            v{fmtV(version)}
                          </span>
                          <StatusBadge status={status} />
                        </div>
                        <p className="tw-text-sm tw-text-slate-700 tw-font-medium tw-leading-snug tw-line-clamp-2">
                          {title}
                        </p>
                      </div>

                      {/* Action row — kept below the title so it never squeezes it */}
                      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-2 tw-gap-y-1.5 tw-mt-2.5">
                        <span className="tw-text-[11px] tw-text-slate-400 tw-shrink-0">
                          {release_date_formatted}
                        </span>
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-ml-auto">
                          {/* Primary state action: solid CTA to publish a draft,
                              subtle ghost to unpublish a live entry */}
                          {isPublished ? (
                            <button
                              type="button"
                              title="Unpublish — hide from users"
                              onClick={() => handlePublishToggle(entry)}
                              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-7 tw-px-2.5 tw-rounded-md tw-text-xs tw-font-semibold tw-text-slate-600 tw-bg-white hover:tw-bg-slate-100 hover:tw-text-slate-800 tw-border tw-border-slate-200 tw-cursor-pointer tw-transition-colors"
                            >
                              <FiEyeOff size={13} /> Unpublish
                            </button>
                          ) : (
                            <button
                              type="button"
                              title="Publish — notify active users"
                              onClick={() => handlePublishToggle(entry)}
                              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-7 tw-px-2.5 tw-rounded-md tw-text-xs tw-font-semibold tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-border-0 tw-shadow-sm tw-cursor-pointer tw-transition-colors"
                            >
                              <FiUploadCloud size={13} /> Publish
                            </button>
                          )}

                          {/* Divider separates the state action from utilities */}
                          <span className="tw-w-px tw-h-5 tw-bg-slate-200" />

                          {/* Utility icon buttons — preflight is off, so each
                              button must reset the UA border/background itself */}
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => openEdit(entry)}
                            className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-text-slate-400 tw-bg-transparent tw-border-0 hover:tw-bg-blue-50 hover:tw-text-blue-600 tw-cursor-pointer tw-transition-colors"
                          >
                            <FiEdit2 size={15} />
                          </button>

                          <button
                            type="button"
                            title="Delete"
                            onClick={() => handleDelete(entry)}
                            className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-text-slate-400 tw-bg-transparent tw-border-0 hover:tw-bg-red-50 hover:tw-text-red-600 tw-cursor-pointer tw-transition-colors"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          )}
        </div>
      </div>

      <ChangelogForm
        key={showForm ? `open-${editEntry?.id ?? 'new'}` : 'closed'}
        show={showForm}
        entry={editEntry}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </>
  );
};

ChangelogAdminPanel.propTypes = {
  onEntrySelect: PropTypes.func,
  onEntryUpdated: PropTypes.func,
};

export default ChangelogAdminPanel;
